import React, { useCallback, useEffect, useState } from "react";
import type { ModalProps, ModalSize } from "./Modal.types";

const sizeClasses: Record<ModalSize, string> = {
    sm: "max-w-sm",
    md: "max-w-md",
    lg: "max-w-lg",
    xl: "max-w-5xl",
};

const Modal: React.FC<ModalProps> = (
    {
        open,
        onClose,
        title,
        children,
        footer,
        size = "md",
    }) => {
    const [mounted, setMounted] = useState<boolean>(open);
    const [visible, setVisible] = useState<boolean>(false);

    const handleEsc = useCallback((e: KeyboardEvent) => {
        if (e.key === "Escape") {
            onClose();
        }
    }, [onClose]);

    useEffect(() => {
        if (open) {
            document.addEventListener("keydown", handleEsc);
        } else {
            document.removeEventListener("keydown", handleEsc);
        }
        return () => document.removeEventListener("keydown", handleEsc);
    }, [open, handleEsc]);

    useEffect(() => {
        let timeoutId: ReturnType<typeof setTimeout> | null = null;
        if (open) {
            setMounted(true);
            requestAnimationFrame(() => setVisible(true));
        } else {
            setVisible(false);
            timeoutId = setTimeout(() => setMounted(false), 200);
        }
        return () => {
            if (timeoutId) clearTimeout(timeoutId);
        };
    }, [open]);

    if (!mounted) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div
                role="presentation"
                className={`absolute inset-0 bg-black/50 transition-opacity duration-200 ${visible ? "opacity-100" : "opacity-0"}`}
                onClick={onClose}
                aria-hidden
            />
            <dialog
                open
                className={`relative w-full ${sizeClasses[size]} max-h-[85vh] flex flex-col rounded-lg bg-white shadow-xl border border-gray-200 transition-opacity duration-200 ${visible ? "opacity-100" : "opacity-0"}`}
            >
                <div className="flex items-start justify-between px-4 py-3 border-b border-b-gray-300">
                    <h1 className="text-lg font-semibold">{title}</h1>
                    <button
                        type="button"
                        aria-label="Close"
                        onClick={onClose}
                        className="inline-flex h-6 w-6 items-center justify-center text-black/50 hover:text-black/75 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                        <svg className="h-4 w-4" viewBox="0 0 16 16" fill="currentColor" aria-hidden>
                            <path
                                d="M.293.293a1 1 0 0 1 1.414 0L8 6.586 14.293.293a1 1 0 1 1 1.414 1.414L9.414 8l6.293 6.293a1 1 0 0 1-1.414 1.414L8 9.414l-6.293 6.293A1 1 0 0 1 .293 14.293L6.586 8 .293 1.707a1 1 0 0 1 0-1.414z"/>
                        </svg>
                    </button>
                </div>
                <div className="px-4 py-6 overflow-y-auto flex-1">
                    {children}
                </div>
                {footer && (
                    <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-t-gray-300">
                        {footer}
                    </div>
                )}
            </dialog>
        </div>
    );
};

export default Modal;
